import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { cn } from "../../../../util/classNameConstructor";
import ListItem from "../../../core/list/ListItem";
import Select from "../../../core/select/Select";
import { HandleItem } from "../components/HandleItem";
import OptimizedInvisibleInput from "../components/OptimizedInvisibleInput";
import {
  type InputColumn,
  type InputRow,
  type Page,
  type PageType,
  pageTypeConfig,
} from "../survey.config";
import type { useEditQuestion } from "../utils";

type RankingRowProps = ReturnType<typeof useEditQuestion> & {
  row: InputRow<"ranking">;
  options: {
    columns: InputColumn<"ranking">[];
  };
  rank: number | string;
  totalOptions: number;
  onRankChange: (newRank: number | null) => void;
  mode: "edit" | "display";
  currentPage: Page<"ranking">;
  index: number;
  limitRanking: number;
  type: PageType;
  value: string[];
  update: (value: string, uid: string) => void;
};

export const RankingRow: React.FC<RankingRowProps> = ({
  index,
  row,
  totalOptions,
  onRankChange,
  mode,
  update,
  currentPage,
  limitRanking,
  type,
  value,
  ...props
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const [active, setActive] = useState(false);

  const rankOptions = useMemo(() => {
    return [
      ...Array.from({ length: limitRanking }, (_, i) => ({
        value: (i + 1).toString(),
        text: (i + 1).toString(),
        className: "h-8",
      })),
    ];
  }, [limitRanking]);

  const handleRankChange = (value: string | string[] | null) => {
    if (value === "–" || value === null) {
      onRankChange(null);
    } else {
      const rankValue = Array.isArray(value) ? value[0] : value;
      onRankChange(Number.parseInt(rankValue, 10));
    }
  };

  const text = useMemo(() => {
    const index = value?.findIndex((item) => item === row.uid);
    return index > -1 ? (index + 1).toString() : "–";
  }, [value, row.uid]);

  const defaults = pageTypeConfig[type]?.defaults;
  const minColumns =
    typeof defaults === "function"
      ? defaults()?.minColumns || 0
      : defaults?.minColumns || 0;

  return (
    <div className={cn("group/cell flex w-full items-center relative")}>
      <HandleItem
        active={active}
        deletable={props.options?.columns.length > minColumns}
        draggable
        mode="edit"
        // className="-ml-2.5 -mr-5"
        className="absolute -translate-x-5"
        handleDelete={() => {
          props.removeItem(row.uid, "columns", { focus: true });
        }}
        axis="row"
      />
      <ListItem
        tag="div"
        noTooltip={mode === "edit"}
        className="w-auto grow"
        primaryContentProps={{
          className: "line-clamp-none",
        }}
        primaryContent={
          <div className="flex items-center justify-between gap-1">
            <Select
              data={rankOptions}
              text={text}
              key={text}
              onValueChange={handleRankChange}
              useTriggerWidth
              size="md"
              className={cn(
                totalOptions >= 10 ? "w-20" : "w-16",
                mode === "edit" && "pointer-events-none",
              )}
            />

            <OptimizedInvisibleInput
              id={row.uid}
              size="md"
              value={translation?.[row.label || ""] || row.label || ""}
              onChange={({ target }) => {
                update?.(target.value, row.uid);
              }}
              placeholder={`${t("global:option")} ${index + 1}`}
              onClick={(e) => e.stopPropagation()}
              classNames={{
                inputContainer: "mx-0 ms-0",
              }}
              onFocus={() => setActive(true)}
              onBlur={() => setActive(false)}
              mode={mode}
            />
          </div>
        }
        classNames={{
          wrapper: "w-full",
          base: cn(
            "my-0.5 h-auto min-h-[38px] gap-0 p-0",
            mode === "edit" && "cursor-grab",
          ),
        }}
        lines={1}
        hoverable={false}
      />
    </div>
  );
};
